import React, { useState, useEffect, useCallback, useMemo } from 'react'
import Button from '@renderer/components/Button'
import { useTranslation } from 'react-i18next'
import { FaTimes, FaPrint, FaChevronDown, FaChevronRight, FaCheckCircle, FaClock, FaCloudUploadAlt } from 'react-icons/fa'
import { FaSpinner } from 'react-icons/fa6'
import { PrintUtils } from '@renderer/utils/printUtils'
import { usePrintStore } from '@renderer/stores/printStore'
import cogoToast from '@successtar/cogo-toast'
import { logPrintEvent } from '@renderer/utils/printEventLogger'

interface UploadStatus {
  isFullyUploaded: boolean
  uploadProgress: number
}

interface SessionData {
  sessionId: string
  createdAt: number
}
interface StripPhoto {
  sessionId: string
  name: string
  path: string
  base64: string | null
  createdAt: number
  frameId?: string | null
}

interface DayGroup {
  dateKey: string
  dateLabel: string
  sessions: SessionData[]
}

interface ReprintModalProps {
  isOpen: boolean
  onClose: () => void
}

// v2.1.0-2.1.2 have listSessions + getSessionPhotos only; getMediaUploadStatus and retryUpload were added later.
// When missing, we hide status/retry UI to avoid errors and show a neutral experience.
const hasReprintStatusSupport = (): boolean =>
  typeof window !== 'undefined' &&
  typeof (window as { api?: { reprint?: { getMediaUploadStatus?: unknown } } }).api?.reprint?.getMediaUploadStatus === 'function'
const hasReprintRetrySupport = (): boolean =>
  typeof window !== 'undefined' &&
  typeof (window as { api?: { reprint?: { retryUpload?: unknown } } }).api?.reprint?.retryUpload === 'function'

const ReprintModal: React.FC<ReprintModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  const [sessions, setSessions] = useState<SessionData[]>([])
  // Map of sessionId -> StripPhoto[]
  const [stripsCache, setStripsCache] = useState<Map<string, StripPhoto[]>>(new Map())
  // Set of sessionIds that have been loaded (even if empty strips)
  const [loadedSessionIds, setLoadedSessionIds] = useState<Set<string>>(new Set())
  const [loadingDays, setLoadingDays] = useState<Set<string>>(new Set())

  const [selectedStrip, setSelectedStrip] = useState<StripPhoto | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)
  const [collapsedDays, setCollapsedDays] = useState<Set<string>>(new Set())
  const [uploadStatuses, setUploadStatuses] = useState<Map<string, UploadStatus>>(new Map())
  const [retryingSession, setRetryingSession] = useState<string | null>(null)

  const showStatusAndRetry = hasReprintStatusSupport() && hasReprintRetrySupport()

  // Load sessions on open
  useEffect(() => {
    if (isOpen) {
      loadSessions()
    } else {
      // Reset state when closed
      setSessions([])
      setStripsCache(new Map())
      setLoadedSessionIds(new Set())
      setSelectedStrip(null)
      setCollapsedDays(new Set())
      setLoadingDays(new Set())
    }
  }, [isOpen])



  const loadSessions = async (): Promise<void> => {
    setIsLoading(true)
    try {
      if (
        typeof window === 'undefined' ||
        !window.api ||
        // @ts-ignore - reprint API is added in preload but not yet in types
        !window.api.reprint ||
        // @ts-ignore - reprint API checks
        typeof window.api.reprint.listSessions !== 'function'
      ) {
        console.warn('Reprint API not available - running in web environment')
        setIsLoading(false)
        return
      }

      const sessionsResult = await window.api.reprint.listSessions()
      if (!sessionsResult.success || !sessionsResult.sessions) {
        console.error('Failed to load sessions:', sessionsResult.error)
        return
      }

      const allSessions: SessionData[] = sessionsResult.sessions
      setSessions(allSessions)

      // Fetch upload status only when API exists (v2.1.5+; v2.1.0-2.1.2 lack getMediaUploadStatus)
      const uniqueSessionIds = allSessions.map((s) => s.sessionId)
      const statusMap = new Map<string, UploadStatus>()
      if (hasReprintStatusSupport()) {
        await Promise.all(
          uniqueSessionIds.map(async (sid) => {
            try {
              // @ts-ignore - reprint API exists when hasReprintStatusSupport()
              const result = await window.api.reprint.getMediaUploadStatus(sid)
              if (result.success && result.status) {
                statusMap.set(sid, {
                  isFullyUploaded: result.status.isFullyUploaded,
                  uploadProgress: result.status.uploadProgress
                })
              }
            } catch {
              // Silently skip failed status checks
            }
          })
        )
      }
      setUploadStatuses(statusMap)
    } catch (error) {
      console.error('Failed to load sessions:', error)
      cogoToast.error(t('reprint.errorLoadingSessions', 'Failed to load sessions'))
    } finally {
      setIsLoading(false)
    }
  }

  const fetchStripsForSessions = async (sessionsToLoad: SessionData[]): Promise<void> => {
    if (sessionsToLoad.length === 0) return

    try {
       // Filter out sessions that are already loaded
      const neededSessions = sessionsToLoad.filter(s => !loadedSessionIds.has(s.sessionId))
      if (neededSessions.length === 0) return

      const newStripsMap = new Map(stripsCache)
      const newLoadedIds = new Set(loadedSessionIds)

      const getSessionPhotos =
        typeof window.api?.reprint?.getSessionPhotos === 'function'
          ? window.api.reprint.getSessionPhotos
          : null

      await Promise.all(
        neededSessions.map(async (session) => {
          try {
            if (!getSessionPhotos) {
              newLoadedIds.add(session.sessionId)
              return
            }
            // @ts-ignore - reprint API
            const photosResult = await getSessionPhotos(session.sessionId)
            if (photosResult.success && photosResult.photos) {
              // Filter for strip photos only (filename contains "-strip")
              const stripPhotos = photosResult.photos.filter((photo) =>
                photo.name.toLowerCase().includes('-strip')
              )
              const frameId = photosResult.frameId || null

              const strips = stripPhotos.map((photo) => ({
                sessionId: session.sessionId,
                name: photo.name,
                path: photo.path,
                base64: photo.base64,
                createdAt: session.createdAt * 1000,
                frameId
              }))

              newStripsMap.set(session.sessionId, strips)
            } else {
               newStripsMap.set(session.sessionId, [])
            }
            newLoadedIds.add(session.sessionId)
          } catch (e) {
            console.error(`Failed to load photos for session ${session.sessionId}`, e)
            newLoadedIds.add(session.sessionId) // Mark as loaded even if failed so we don't retry endlessly
          }
        })
      )

      setStripsCache(newStripsMap)
      setLoadedSessionIds(newLoadedIds)
    } catch (error) {
      console.error('Error fetching strips:', error)
    }
  }

  // Group sessions by day
  const groupedSessionsByDay = useMemo((): DayGroup[] => {
    const groups = new Map<string, SessionData[]>()

    // Sort sessions by createdAt descending first
    const sortedSessions = [...sessions].sort((a, b) => b.createdAt - a.createdAt)

    for (const session of sortedSessions) {
      const date = new Date(session.createdAt * 1000)
      const dateKey = date.toISOString().split('T')[0] // YYYY-MM-DD

      if (!groups.has(dateKey)) {
        groups.set(dateKey, [])
      }
      groups.get(dateKey)!.push(session)
    }

    const result: DayGroup[] = []
    // Groups keys are already sorted if we iterate insertion order? No, set/map iteration is insertion order.
    // But let's sort keys explicitly to be safe
    const sortedKeys = Array.from(groups.keys()).sort((a, b) => b.localeCompare(a))

    for (const dateKey of sortedKeys) {
      const date = new Date(dateKey)
      const dateLabel = date.toLocaleDateString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
      result.push({
        dateKey,
        dateLabel,
        sessions: groups.get(dateKey)!
      })
    }

    return result
  }, [sessions])

  // Default all day groups to collapsed when sessions are loaded
  useEffect(() => {
    if (groupedSessionsByDay.length > 0) {
      setCollapsedDays(new Set(groupedSessionsByDay.map((group) => group.dateKey)))
    }
  }, [groupedSessionsByDay])

  const handleReprintRequest = useCallback(async (): Promise<void> => {
    if (!selectedStrip?.base64) return

    setIsPrinting(true)
    try {
      const { getPrinterForFrame } = usePrintStore.getState()

      // Get frame-specific printer configuration
      const frameId = selectedStrip.frameId || ''
      const { printerName, settings } = getPrinterForFrame(frameId)

      // Use frame-specific settings, fallback to defaults if frameId is empty
      const result = await PrintUtils.sendToPrinter(selectedStrip.base64, {
        printerName,
        orientation: settings.orientation,
        pageSize: settings.pageSize,
        margins: settings.margins,
        dpi: settings.dpi,
        scaleFactor: settings.scaleFactor || 1,
        printResolutionScale: settings.printResolutionScale || 1,
        printPositionShift: settings.printPositionShift,
        copies: 1 // Default to 1 copy for reprints
      })

      if (result.success) {
        cogoToast.success(t('reprint.printSuccess', 'Print job sent successfully'))
        logPrintEvent({ eventType: 'operator_reprint', copies: 1, printerName, success: true })
      } else {
        cogoToast.error(result.error || t('reprint.printError', 'Failed to print'))
      }
    } catch (error) {
      console.error('Failed to print:', error)
      cogoToast.error(t('reprint.printError', 'Failed to print'))
    } finally {
      setIsPrinting(false)
    }
  }, [selectedStrip, t])

  const handleRetryUpload = useCallback(async (sessionId: string, e?: React.MouseEvent): Promise<void> => {
    if (e) {
      e.stopPropagation()
    }
    if (typeof window.api?.reprint?.retryUpload !== 'function') {
      cogoToast.error(t('reprint.retryError', 'Failed to retry upload'))
      return
    }
    setRetryingSession(sessionId)
    try {
      // @ts-ignore - reprint API
      const result = await window.api.reprint.retryUpload(sessionId)
      if (result.success && result.recovered) {
        const total = result.recovered.photos + result.recovered.videos + result.recovered.gifs
        if (total > 0) {
          cogoToast.success(
            t('reprint.retrySuccess', `Queued ${total} file(s) for upload`, { count: total })
          )
        } else {
          cogoToast.info(t('reprint.retryNoFiles', 'No orphaned files found — uploads may already be in progress'))
        }
      } else {
        cogoToast.error(result.error || t('reprint.retryError', 'Failed to retry upload'))
      }

      // Refresh upload status for this session
      // @ts-ignore - reprint API
      if (window.api?.reprint?.getMediaUploadStatus) {
        try {
          // @ts-ignore - reprint API
          const statusResult = await window.api.reprint.getMediaUploadStatus(sessionId)
          if (statusResult.success && statusResult.status) {
            const s = statusResult.status
            setUploadStatuses((prev) => {
              const newMap = new Map(prev)
              newMap.set(sessionId, {
                isFullyUploaded: s.isFullyUploaded,
                uploadProgress: s.uploadProgress
              })
              return newMap
            })
          }
        } catch {
          // Silently skip
        }
      }
    } catch (error) {
      console.error('Failed to retry upload:', error)
      cogoToast.error(t('reprint.retryError', 'Failed to retry upload'))
    } finally {
      setRetryingSession(null)
    }
  }, [t])

  const toggleDayCollapse = async (dateKey: string): Promise<void> => {
    const isExpanding = collapsedDays.has(dateKey)
    
    setCollapsedDays((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(dateKey)) {
        newSet.delete(dateKey)
      } else {
        newSet.add(dateKey)
      }
      return newSet
    })

    if (isExpanding) {
      // Find the group and load strips for it
      const group = groupedSessionsByDay.find(g => g.dateKey === dateKey)
      if (group && group.sessions.length > 0) {
        // Check if we need to load any sessions
        const sessionsToLoad = group.sessions.filter(s => !loadedSessionIds.has(s.sessionId))
        
        if (sessionsToLoad.length > 0) {
          setLoadingDays(prev => new Set(prev).add(dateKey))
          await fetchStripsForSessions(sessionsToLoad)
          setLoadingDays(prev => {
            const newSet = new Set(prev)
            newSet.delete(dateKey)
            return newSet
          })
        }
      }
    }
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
        <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-carbon">
              {selectedStrip
                ? t('reprint.preview', 'Preview')
                : t('reprint.title', 'Reprint Strip Photos')}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors p-1"
              title={t('common.close', 'Close')}
            >
              <FaTimes className="text-xl" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
              </div>
            ) : selectedStrip ? (
              /* Strip Preview */
              <div className="flex flex-col items-center gap-4">
                <img
                  src={selectedStrip.base64 || ''}
                  alt={selectedStrip.name}
                  className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-lg"
                />
                <div className="text-center">
                  <p className="font-medium text-carbon">
                    {t('reprint.session', 'Session')}: {selectedStrip.sessionId}
                  </p>
                  <p className="text-sm text-gray-500">
                    {new Date(selectedStrip.createdAt).toLocaleDateString(undefined, {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            ) : groupedSessionsByDay.length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                {t('reprint.noSessions', 'No sessions found')}
              </p>
            ) : (
              /* Collapsible sections by day */
              <div className="space-y-4">
                {groupedSessionsByDay.map((group) => {
                  const isLoadingDay = loadingDays.has(group.dateKey)
                  const isCollapsed = collapsedDays.has(group.dateKey)
                  
                  // Calculate stats (only when status API is available)
                  const sessionCount = group.sessions.length
                  const uploadedCount = showStatusAndRetry
                    ? group.sessions.filter((s) => uploadStatuses.get(s.sessionId)?.isFullyUploaded).length
                    : 0
                  const allUploaded = showStatusAndRetry ? uploadedCount === sessionCount : true

                  // Collect all loaded strips for this group
                  const groupStrips: StripPhoto[] = []
                  group.sessions.forEach(s => {
                    const sessionStrips = stripsCache.get(s.sessionId)
                    if (sessionStrips) {
                      groupStrips.push(...sessionStrips)
                    }
                  })

                  // Sort strips by time (createdAt) descending
                  groupStrips.sort((a, b) => b.createdAt - a.createdAt)

                  // Helper: upload status badge (only when status/retry API available; v2.1.0-2.1.2 lack it)
                  const renderUploadBadge = (): JSX.Element | null => {
                    if (!showStatusAndRetry) return null
                    return (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        allUploaded ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {uploadedCount}/{sessionCount} {t('reprint.uploaded', 'uploaded')}
                      </span>
                    )
                  }

                  return (
                  <div key={group.dateKey} className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* Day header - clickable to collapse */}
                    <button
                      type="button"
                      onClick={() => toggleDayCollapse(group.dateKey)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {isCollapsed ? (
                          <FaChevronRight className="text-gray-500" />
                        ) : (
                          <FaChevronDown className="text-gray-500" />
                        )}
                        <span className="font-semibold text-carbon">{group.dateLabel}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {renderUploadBadge()}
                        {showStatusAndRetry && !allUploaded && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              const notUploaded = group.sessions.filter(
                                (s) => !uploadStatuses.get(s.sessionId)?.isFullyUploaded
                              )
                              notUploaded.forEach((s) => handleRetryUpload(s.sessionId))
                            }}
                            className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors flex items-center gap-1"
                            title={t('reprint.retryUploadAll', 'Retry upload for all pending sessions')}
                          >
                            <FaCloudUploadAlt className="text-xs" />
                            {t('reprint.retryAll', 'Retry All')}
                          </button>
                        )}
                        <span className="text-sm text-gray-500">
                           {/* Show session count initially, or strip count if loaded? 
                               User expects "trips" count maybe. 
                               But we don't know strip count until loaded. 
                               Let's show "X session(s)" to be accurate.
                           */}
                           {sessionCount} {t('reprint.sessionCount', 'session(s)')}
                        </span>
                      </div>
                    </button>

                    {/* Strips grid - collapsible */}
                    {!isCollapsed && (
                      <div className="p-4">
                         {isLoadingDay ? (
                            <div className="flex justify-center py-8">
                                <FaSpinner className="animate-spin text-blue-500 text-2xl" />
                                <span className="ml-2 text-gray-500">{t('common.loading', 'Loading photos...')}</span>
                            </div>
                         ) : groupStrips.length === 0 ? (
                            <div className="text-center text-gray-400 text-sm py-4">
                                {t('reprint.noStripsInSession', 'No strip photos found in these sessions')}
                            </div>
                         ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                {groupStrips.map((strip, index) => {
                                  // Upload status indicator (only when status API available)
                                  const renderUploadStatus = (): JSX.Element | null => {
                                    if (!showStatusAndRetry) return null
                                    const status = uploadStatuses.get(strip.sessionId)
                                    if (!status) return (
                                      <span className="absolute bottom-1 right-1 bg-white rounded-full p-1 shadow-sm" title={t('reprint.uploadPending', 'Upload pending')}>
                                        <FaClock className="text-gray-400 text-xs" />
                                      </span>
                                    )
                                    if (status.isFullyUploaded) return (
                                      <span className="absolute bottom-1 right-1 bg-white rounded-full p-1 shadow-sm" title={t('reprint.uploadComplete', 'Fully uploaded')}>
                                        <FaCheckCircle className="text-green-500 text-xs" />
                                      </span>
                                    )
                                    return (
                                      <span className="absolute bottom-1 right-1 bg-white rounded-full p-1 shadow-sm" title={`${t('reprint.uploading', 'Uploading')} ${status.uploadProgress}%`}>
                                        <FaSpinner className="animate-spin text-blue-500 text-xs" />
                                      </span>
                                    )
                                  }

                                  const isRetrying = retryingSession === strip.sessionId
                                  const sessionStatus = uploadStatuses.get(strip.sessionId)
                                  const showRetry = showStatusAndRetry && !sessionStatus?.isFullyUploaded

                                  return (
                                    <button
                                      key={`${strip.sessionId}-${strip.name}-${index}`}
                                      type="button"
                                      onClick={() => setSelectedStrip(strip)}
                                      className="relative bg-gray-100 rounded-lg overflow-hidden hover:ring-2 hover:ring-blue-500 transition-all shadow-sm hover:shadow-md p-2"
                                    >
                                      {strip.base64 ? (
                                        <img
                                          src={strip.base64}
                                          alt={strip.name}
                                          className="w-full h-auto rounded"
                                        />
                                      ) : (
                                        <div className="w-full h-32 flex items-center justify-center text-gray-400 text-xs">
                                          {t('reprint.loadError', 'Failed to load')}
                                        </div>
                                      )} 
                                      {/* Upload status indicator */}
                                      {renderUploadStatus()}
                                      {/* Retry upload button */}
                                      {showRetry && (
                                        <button
                                          type="button"
                                          onClick={(e) => handleRetryUpload(strip.sessionId, e)}
                                          disabled={isRetrying}
                                          className="absolute top-1 right-1 bg-white rounded-full p-1.5 shadow-sm hover:bg-blue-50 transition-colors disabled:opacity-50"
                                          title={t('reprint.retryUpload', 'Retry upload')}
                                        >
                                          {isRetrying ? (
                                            <FaSpinner className="animate-spin text-blue-500 text-xs" />
                                          ) : (
                                            <FaCloudUploadAlt className="text-blue-500 text-xs" />
                                          )}
                                        </button>
                                      )}
                                    </button>
                                  )
                                })}
                            </div>
                         )}
                      </div>
                    )}
                  </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div>
              {selectedStrip ? (
                <Button
                  text={t('common.back', 'Back')}
                  variant="secondary"
                  onClick={() => setSelectedStrip(null)}
                  className="!px-6 !py-2 !text-base"
                />
              ) : (
                <div className="text-sm text-gray-500">
                  {sessions.length > 0 && (
                    <span>
                      {sessions.length} {t('reprint.totalSessions', 'total session(s)')} • {groupedSessionsByDay.length}{' '}
                      {t('reprint.days', 'day(s)')}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                text={t('common.close', 'Close')}
                variant="secondary"
                onClick={onClose}
                className="!px-6 !py-2 !text-base"
              />
              {selectedStrip && (
                <Button
                  text={
                    isPrinting
                      ? t('reprint.printing', 'Printing...')
                      : t('reprint.printButton', 'Print')
                  }
                  onClick={handleReprintRequest}
                  className="!px-6 !py-2 !text-base"
                  disabled={isPrinting || !selectedStrip.base64}
                >
                  <FaPrint className="mr-2" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      
    </>
  )
}

export default ReprintModal
